import { useState, useEffect, useRef, Fragment, lazy, Suspense } from 'react';
import { ZZ_OPEN_LABEL } from './opening';
import { Arrow, Chevron, ZuzuStack } from './icons';
import { ZZ_MODALITIES, ZZ_TIERS, ZZ_SITE_PATHS, ZZ_EVENT_LINKS } from './data';
import { SMobileMenuButton, SMobileMenu, SMobileTabBar, SMobileAppHeader, mBackTarget } from './mobile-nav';
import { appDownloadUrl, goesToStore } from './storeLinks';
import { WaveBreak } from './WaveBreak';
import { SPeakHours } from './PeakHours';
import { CancelPolicyModal } from './CancelPolicyModal';
// The 5-step signup wizard (health screening, face capture, DOB/dial-code
// pickers) is only reached by visitors who actually navigate to a signup
// page — lazy-loading it keeps that weight off every marketing pageview
// (Lighthouse "reduce unused JavaScript"). WaAuth.tsx lazy-loads the same
// module's AppSignup for the app-side signed-out gate, so this chunk is
// shared between both entry points.
const PageSignup = lazy(() =>
  import('./signup-shell').then((m) => ({ default: m.PageSignup })),
);
// About/contact/legal each carry their own content + dedicated stylesheet
// (about.css / contact.css) that only that page needs — lazy-loading them
// keeps that weight off every other marketing pageview, same reasoning as
// the signup wizard above (Lighthouse "reduce unused JS/CSS").
const PageAbout = lazy(() => import('./PageAbout'));
const PageContact = lazy(() => import('./PageContact'));
const PageLegal = lazy(() => import('./PageLegal'));
// Same reasoning: the equipment index, per-room detail pages and pricing
// page are each a meaningful chunk of markup/icons a homepage (or about/
// contact) visitor never needs.
const PageModalities = lazy(() => import('./PageModalities'));
const PageModalityDetail = lazy(() => import('./PageModalityDetail'));
const PagePricing = lazy(() => import('./PagePricing'));
// The public booking page reads live availability through __ZZ_ACTIONS and
// carries its own picker + stylesheet — lazy for the same reason as the rest.
const PageBook = lazy(() => import('./PageBook'));

/* ZUZU marketing site - multi-page components.

   Pages: home / modalities / pricing / about / contact
   Routing: simple state lifted in App; nav links call onNav(page).
*/

/* ZUZU marketing site - multi-page components.
   Loaded after React, Babel, icons.jsx, data.js. Shares window globals.

   Pages: home / modalities / pricing / about / contact
   Routing: simple state lifted in App; nav links call onNav(page).
*/


/* ─────────────────────────────────────────────────────────
   Announcement bar
   ───────────────────────────────────────────────────────── */
export const SAnnounce = () =>
<div className="announce">
    <span><span className="dot"></span>{ZZ_OPEN_LABEL}</span>
    <span className="sep">·</span>
    <span>century city, cape town</span>
    <span className="sep">·</span>
    <a href={ZZ_SITE_PATHS['signup:monthly']} onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('zz-nav', { detail: 'signup:monthly' })); }}>become a member <Arrow size={10} /></a>
  </div>;


/* ─────────────────────────────────────────────────────────
   Nav - multi-page aware
   ───────────────────────────────────────────────────────── */
export const SNav = ({ page, onNav, onApp, onAuth, onOpenMenu }) => {
  const onModality = typeof page === 'string' && page.startsWith('modality:');
  return (
<nav className="nav">
    <div className="inner" data-comment-anchor="dd54dd6cc9-div-28-5">
      <a className="wordmark" href={ZZ_SITE_PATHS.home} onClick={(e) => { e.preventDefault(); onNav('home'); }} aria-label="zuzu home">
        <ZuzuStack size={32} />
        <span className="city">century city</span>
      </a>
      <div className="links">
        <a className={page === 'about' ? 'active' : ''} href={ZZ_SITE_PATHS.about} onClick={(e) => { e.preventDefault(); onNav('about'); }}>about</a>
        {/* "rooms" carries the room list. It and the old "experiences" tab both
            pointed at /equipment with the same five rooms behind them, so one
            was doing nothing — that slot is now "events" below. */}
        <div className="nav-drop">
          <a className={page === 'modalities' || onModality ? 'active' : ''} aria-haspopup="true"
        href={ZZ_SITE_PATHS.modalities} onClick={(e) => { e.preventDefault(); onNav('modalities'); }}>rooms <Chevron size={11} /></a>
          <div className="nav-drop-menu" role="menu">
            {(ZZ_MODALITIES || []).map((m) =>
          <a key={m.id} role="menuitem" className={page === 'modality:' + m.id ? 'active' : ''}
          href={ZZ_SITE_PATHS['modality:' + m.id] || ZZ_SITE_PATHS.modalities}
          onClick={(e) => { e.preventDefault(); onNav('modality:' + m.id); }}>
              {m.name}{m.temp && <span className="d">{m.temp}</span>}
            </a>
          )}
            <a role="menuitem" className={'nav-drop-all' + (page === 'modalities' ? ' active' : '')}
          href={ZZ_SITE_PATHS.modalities} onClick={(e) => { e.preventDefault(); onNav('modalities'); }}>
              all five rooms
            </a>
          </div>
        </div>
        {/* The occasion/event pages were only reachable from the homepage grid
            and the footer; they now have a nav home of their own. */}
        <div className="nav-drop">
          <a aria-haspopup="true"
        href={ZZ_SITE_PATHS['gift-a-session']} onClick={(e) => { e.preventDefault(); onNav('gift-a-session'); }}>events <Chevron size={11} /></a>
          <div className="nav-drop-menu nav-drop-menu--events" role="menu">
            {ZZ_EVENT_LINKS.map((ev) =>
          <a key={ev.id} role="menuitem"
          href={ZZ_SITE_PATHS[ev.id]}
          onClick={(e) => { e.preventDefault(); onNav(ev.id); }}>
              {ev.label}
            </a>
          )}
          </div>
        </div>
        <a className={page === 'book' ? 'active' : ''} href={ZZ_SITE_PATHS.book} onClick={(e) => { e.preventDefault(); onNav('book'); }}>book</a>
        <a className={page === 'pricing' ? 'active' : ''} href={ZZ_SITE_PATHS.pricing} onClick={(e) => { e.preventDefault(); onNav('pricing'); }}>pricing</a>
        <a className={page === 'contact' ? 'active' : ''} href={ZZ_SITE_PATHS.contact} onClick={(e) => { e.preventDefault(); onNav('contact'); }}>visit</a>
      </div>
      <div className="actions">
        {/* One header action: "sign in" — returning members reach for the
            top-right corner, and the signup route is already the primary CTA
            in the hero, pricing and footer. ("open app" was dropped from the
            desktop header; the mobile drawer and tab bar keep their own app
            entry points.) */}
        <a className="btn btn--primary" href={ZZ_SITE_PATHS.auth}
        onClick={(e) => { e.preventDefault(); onAuth ? onAuth() : onNav('auth'); }}>sign in <Arrow /></a>
        <SMobileMenuButton onOpen={onOpenMenu} />
      </div>
    </div>
  </nav>);
};


/* ─────────────────────────────────────────────────────────
   Live status strip
   ───────────────────────────────────────────────────────── */
// Pre-launch: the public homepage shows the five rooms (no fabricated live
// occupancy). Real "free right now" counts are shown to signed-in members on the
// Today screen via get_live_capacity once the club is open.
export const SLive = () => {
  // Clear, concise descriptor per room (not the internal "eyebrow" word).
  const ZZ_ROOM_DETAIL = { plunge: '7 & 12°C', sauna: '90°C', red: 'infrared', comp: 'normatec boots', inbody: '5-min scan' };
  return (
    <section className="live" data-comment-anchor="d0f8004b29-section-68-5">
      <div className="inner">
        <div className="label">the five rooms</div>
        <div className="pills">
          {ZZ_MODALITIES.map((m, i) =>
          <span key={i} className="live-pill">
              {m.name} · <b>{ZZ_ROOM_DETAIL[m.id] || m.eyebrow}</b>
            </span>
          )}
        </div>
      </div>
    </section>);

};

/* ─────────────────────────────────────────────────────────
   HERO - four layouts (portal / stage / bleed / quiet) via tweaks
   "portal" is the new default - editorial split with the arch
   photo on the right. "stage" is a centered, cinema variant.
   ───────────────────────────────────────────────────────── */
const HERO_ROOMS = [
['01', 'cold plunge'],
['02', 'nordic sauna'],
['03', 'red light'],
['04', 'normatec compression'],
['05', 'inbody scan']];


const HeroChips = () =>
<ul className="hero-chips" aria-label="five rooms">
    {HERO_ROOMS.map(([n, label]) =>
  <li key={n} className="hero-chip">
        <span className="n">{n}</span>
        <span className="t">{label}</span>
      </li>
  )}
  </ul>;


const HeroMeta = () =>
<div className="hero-meta-row">
    <div className="hm-item">
      <span className="k"></span>
      <span className="v"></span>
    </div>
    <div className="hm-sep" aria-hidden="true"></div>
    <div className="hm-item">
      <span className="k"></span>
      <span className="v"></span>
    </div>
    <div className="hm-sep" aria-hidden="true"></div>
    <div className="hm-item hm-app">
      <span className="k"></span>
      <span className="v"></span>
    </div>
  </div>;


/* Where "download the app" points for THIS visitor: the App Store on an
   iPhone, Play on an Android, and the web app on a desktop (or on a platform
   whose listing isn't configured yet — see storeLinks.ts).

   Resolved after mount rather than during render. The SEO prerender only
   bakes <head> tags (scripts/seo/prerender.mjs) and leaves the body to the
   client, so there is no mismatch to avoid today — but reading navigator in
   an effect keeps it that way if the body is ever prerendered too, and the
   fallback href is a working destination in the meantime. */
const useAppDownload = () => {
  const [dest, setDest] = useState({ href: ZZ_SITE_PATHS.app, external: false });
  useEffect(() => {
    setDest({ href: appDownloadUrl(ZZ_SITE_PATHS.app), external: goesToStore() });
  }, []);
  return dest;
};

const HeroCtas = () => {
  const app = useAppDownload();
  return (
<div className="ctas">
    {/* Mobile-only (hidden on desktop via .btn--book-mobile): booking is the
        top job-to-be-done on a phone, so it gets the first CTA slot. */}
    <a className="btn btn--primary btn--lg btn--book-mobile" href={ZZ_SITE_PATHS.book} onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('zz-nav', { detail: 'book' })); }}>book now <Arrow /></a>
    <a className="btn btn--primary btn--lg" href={ZZ_SITE_PATHS['signup:monthly']} onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('zz-nav', { detail: 'signup:monthly' })); }}>become a member <Arrow /></a>
    <a className="btn btn--ghost btn--lg btn--app" href={app.href}
       onClick={app.external ? undefined : (e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('zz-nav', { detail: 'app' })); }}>
      <span className="dl-glyph" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="6" y="2.5" width="12" height="19" rx="2.4" />
          <line x1="11" y1="18.4" x2="13" y2="18.4" />
        </svg>
      </span>
      download our app
    </a>
  </div>);
};


const HeroCopy = ({ withChips = false, withMeta = false }) =>
<div className="hero-copy">
    <span className="eyebrow eyebrow--accent">self-service recovery & wellness club</span>
    <h1>recovery,<br /><em>for you</em></h1>
    <p className="lede">cold plunge, nordic sauna, compression boots, red light and an inbody scan - under one beautiful roof. book online. choose your room. leave restored.

  </p>
    {withChips && <HeroChips />}
    <HeroCtas />
    {withMeta && <HeroMeta />}
  </div>;


const HeroArchFrame = () =>
<div className="hero-arch" aria-hidden="true">
    <img
      src="/redesign/assets/imagery/zuzu-reception-arch-v2-768.618b51ee.webp"
      srcSet="/redesign/assets/imagery/zuzu-reception-arch-v2-480.82d43f89.webp 480w, /redesign/assets/imagery/zuzu-reception-arch-v2-768.618b51ee.webp 768w, /redesign/assets/imagery/zuzu-reception-arch-v2-1120.6f57ae68.webp 1120w, /redesign/assets/imagery/zuzu-reception-arch-v2.78ad1501.webp 1448w"
      sizes="(max-width: 640px) 92vw, 560px"
      width="1448" height="1931"
      alt="" fetchpriority="high" decoding="async" />
    <div className="hero-arch-cap">
      <span className="cap-eyebrow">century city · cape town</span>
      <span className="cap-meta">{ZZ_OPEN_LABEL}</span>
    </div>
  </div>;


export const SHero = ({ layout = 'portal' }) => {
  if (layout === 'stage') {
    return (
      <section className="hero" data-layout="stage">
        <div className="hero-grid">
          <div className="hero-copy hero-copy--centered">
            <span className="eyebrow eyebrow--accent">self-service recovery & wellness club</span>
            <h1>recovery,<br /><em>for you</em></h1>
            <p className="lede">
              cold plunge, nordic sauna, compression boots, red light and an inbody scan - under one beautiful roof.
            </p>
            <HeroCtas />
          </div>
          <div className="hero-stage" aria-hidden="true">
            <img
              src="/redesign/assets/imagery/zuzu-reception-arch-v2-768.618b51ee.webp"
              srcSet="/redesign/assets/imagery/zuzu-reception-arch-v2-480.82d43f89.webp 480w, /redesign/assets/imagery/zuzu-reception-arch-v2-768.618b51ee.webp 768w, /redesign/assets/imagery/zuzu-reception-arch-v2-1120.6f57ae68.webp 1120w, /redesign/assets/imagery/zuzu-reception-arch-v2.78ad1501.webp 1448w"
              sizes="(max-width: 640px) 92vw, 560px"
              width="1448" height="1931"
              alt="" fetchpriority="high" decoding="async" />
            <div className="hero-stage-grain"></div>
          </div>
          <HeroChips />
        </div>
      </section>);

  }
  if (layout === 'bleed') {
    return (
      <section className="hero" data-layout="bleed">
        <div className="hero-bg" aria-hidden="true"></div>
        <div className="hero-grid"><HeroCopy withChips /></div>
      </section>);

  }
  if (layout === 'quiet') {
    return (
      <section className="hero" data-layout="quiet">
        <div className="hero-grid"><HeroCopy withChips /></div>
      </section>);

  }
  // default: portal
  return (
    <section className="hero" data-layout="portal" data-comment-anchor="8fd0972832-section-206-5">
      <div className="hero-grid">
        <HeroCopy withMeta />
        <HeroArchFrame />
      </div>
    </section>);

};

/* ─────────────────────────────────────────────────────────
   MODALITIES grid (home)
   ───────────────────────────────────────────────────────── */
export const SModalities = ({ photoVariant = 0, onNav }) =>
<section className="section modalities" id="modalities">
    <div className="shell">
      <header className="section-head">
        <div>
          <span className="eyebrow eyebrow--accent">five rooms</span>
          <h2>choose your <em>reset.</em></h2>
        </div>
        <p className="blurb">
          whether you're seeking recovery, clarity, focus, or calm, our five
          wellness spaces give you the freedom to create the experience your body
          and mind need today.
        </p>
      </header>
      <div className="grid">
        {ZZ_MODALITIES.map((m) =>
      <a key={m.id} className={'modality modality--' + m.id}
      href={ZZ_SITE_PATHS['modality:' + m.id] || ZZ_SITE_PATHS.modalities}
      onClick={(e) => { e.preventDefault(); onNav('modality:' + m.id); }}>
            <div className="photo">
              <img
            src={m.homeThumb || m.homePhoto || m.photos[0]}
            srcSet={m.homeSrcSet || undefined}
            sizes={m.homeSrcSet ? '(max-width: 760px) 78vw, (max-width: 880px) 46vw, (max-width: 1100px) 31vw, 237px' : undefined}
            alt={m.name}
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', display: 'block', objectFit: m.id === 'inbody' ? 'contain' : 'cover' }} />
              <span className="tag">{m.tag}</span>
            </div>
            <div className="body">
              <h3>{m.name}</h3>
              <p className="blurb">{m.short}</p>
              <div className="stats">
                {m.temp && <div className="s"><div className="k">temp</div><div className="v">{m.temp}</div></div>}
                <div className="s"><div className="k">{m.unitLabel}</div><div className="v">{m.units}</div></div>
              </div>
            </div>
          </a>
      )}
      </div>
    </div>
  </section>;


/* ─────────────────────────────────────────────────────────
   HOW A VISIT WORKS
   ───────────────────────────────────────────────────────── */
const FLOW_ICONS = [
// 01 book - calendar slot
<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="2.5" /><path d="M3.5 9.5h17M8 3v4M16 3v4" /><circle cx="12" cy="14.5" r="1.4" fill="currentColor" stroke="none" /></svg>,
// 02 arrive - face recognition
<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2" /><path d="M9 10v1M15 10v1M12 10v3l-1 1M9.5 15.5c.8.7 1.7 1 2.5 1s1.7-.3 2.5-1" /></svg>,
// 03 move - cycle / repeat
<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12a8 8 0 0 1 13.5-5.8L20 8" /><path d="M20 4v4h-4" /><path d="M20 12a8 8 0 0 1-13.5 5.8L4 16" /><path d="M4 20v-4h4" /></svg>,
// 04 leave - shower + droplets
<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 11h11a0 0 0 0 1 0 0 5.5 5.5 0 0 1-5.5 5.5h0A5.5 5.5 0 0 1 5 11Z" /><path d="M16 11V6a2.5 2.5 0 0 0-2.5-2.5h0A2.5 2.5 0 0 0 11 6" /><path d="M9 20v.5M12 19.5v.5M15 20v.5" /></svg>];


export const SFlow = () =>
<section className="flow">
    <div className="shell">
      <div className="head">
        <span className="eyebrow eyebrow--accent">how a visit works</span>
        <h2>your whole visit,<br /><em>handled for you.</em></h2>
      </div>
      <div className="steps">
        {[
      ['01 · book', 'tap a slot in the app.', 'download our app and ensure you have booked a slot to guarantee availability.', ''],
      ['02 · arrive', 'face recognition lets you in.', 'no front desk, no small talk. towels, water and showers are waiting for you inside.', ''],
      ['03 · move', 'warm up, plunge, rejuvenate, repeat.', 'sauna, then plunge, then decompress. read our FAQ for the recommended order.', ''],
      ['04 · leave', 'shower, rinse, slip out.', '2 showers, soft towels. face ID lets you out and the door locks behind you.', '']].
      map(([n, t, p, time], i) =>
      <div key={i} className="step">
            <div className="step-icon" aria-hidden="true">{FLOW_ICONS[i]}</div>
            <div className="num">{n}</div>
            <h3>{t}</h3>
            <p>{p}</p>
            {time && <div className="time">{time}</div>}
          </div>
      )}
      </div>
    </div>
  </section>;


/* ─────────────────────────────────────────────────────────
   MANIFESTO - full-bleed quote block
   ───────────────────────────────────────────────────────── */
export const SManifesto = () =>
<section className="manifesto">
    <div className="bg" aria-hidden="true"></div>
    <div className="inner" data-comment-anchor="fb1e8cf1d5-div-308-5">
      <span className="eyebrow eyebrow--light">for you</span>
      <blockquote data-comment-anchor="757b794762-blockquote-310-7">
        good for your body, <em>curated relaxation.</em>
      </blockquote>
      <div className="signed"></div>
    </div>
  </section>;


/* ─────────────────────────────────────────────────────────
   MEMBERSHIP block (home + pricing page)
   ───────────────────────────────────────────────────────── */
export const STiers = ({ heading = true, rooms = false }) =>
<section className="membership" data-comment-anchor="eff739ec51-section-322-1">
    <div className="shell">
      {heading &&
    <header className="section-head">
          <div>
            <span className="eyebrow eyebrow--accent">access</span>
            <h2>simple <em>by design.</em></h2>
          </div>
          <p className="blurb">
            three ways to use the room. pay as you go in thirty-minute credits,
            an unlimited monthly membership, or reserve the whole room for your group.
          </p>
        </header>
    }
      <div className="tiers">
        {ZZ_TIERS.map((tier) => <TierCard key={tier.id} tier={tier} />)}
      </div>
    </div>
  </section>;


/* ─── individual tier card. Handles three shapes:
       - flat price        (drop, private)
       - subOptions toggle (monthly off-peak vs anytime)
       The toggle lives inside the card so the user can swap windows
       without leaving the card. ────────────────────────────────── */
export const TierCard = ({ tier }) => {
  const [optIdx, setOptIdx] = useState(0);
  // The cancellation policy, on demand. The tiers used to be written out in
  // the card's own bullets; the card now names the one fact and opens the
  // policy for the rest (see cancelPolicyCopy).
  const [policyOpen, setPolicyOpen] = useState(false);
  const opt = tier.subOptions ? tier.subOptions[optIdx] : null;
  const price = opt ? opt.price : tier.price;
  const per = opt ? opt.per : tier.per;
  const subText = opt ? opt.window : tier.sub;
  return (
    <article className={'tier tier--' + tier.id + (tier.featured ? ' tier--featured' : '') + (opt ? opt.id === 'peak' ? ' win-peak' : opt.id === 'off-peak' ? ' win-offpeak' : opt.id === 'annual' ? ' mem-annual' : opt.id === 'monthly' ? ' mem-monthly' : '' : '')}>
      {tier.badge && <div className="badge">{tier.badge}</div>}
      {tier.eyebrow && <div className="tier-eyebrow">{tier.eyebrow}</div>}
      <div className="name">{tier.name}</div>
      {tier.subOptions &&
      <div className="tier-toggle">
          {tier.subOptions.map((o, i) =>
        <button key={o.id} className={i === optIdx ? 'on' : ''} onClick={() => setOptIdx(i)}>
              <span className="dot" />
              {o.name}
            </button>
        )}
        </div>
      }

      {/* Membership shows two ways to pay, side by side */}
      {tier.priceOptions ?
      <div className="tier-prices">
          {tier.priceOptions.map((p) =>
        <div key={p.id} className={'tier-price-opt' + (p.badge ? ' tier-price-opt--best' : '')}>
              {p.badge && <span className="tpo-flag">{p.badge}</span>}
              <div className="tpo-price">
                <span className="cur">{p.price.charAt(0)}</span>{p.price.slice(1)}
                <span className="per">{p.per}</span>
              </div>
              <div className="tpo-note">{p.note}</div>
            </div>
        )}
        </div> :
      <div className="price">
          <span className="cur">{price.charAt(0)}</span>
          {price.slice(1)}
          <span className="per">{per}</span>
        </div>
      }

      {opt && opt.duration && opt.time ?
      <div className="tier-window" data-comment-anchor="70b1d541e5-div-403-7">
          <span className="tw-duration" data-comment-anchor="484ff6fad6-span-404-11">{opt.duration}</span>
          <span className="tw-sep" aria-hidden="true"></span>
          <span className="tw-time">{opt.time}</span>
        </div> :
      subText ? <div className="tier-sub">{subText}</div> : null}

      <ul>{tier.features.map((f, i) => <li key={i}>{f}</li>)}</ul>
      {tier.cancelPolicy &&
      <>
        <button type="button" className="zz-policy-link tier-policy" aria-haspopup="dialog" onClick={() => setPolicyOpen(true)}>cancellation policy</button>
        <CancelPolicyModal open={policyOpen} onClose={() => setPolicyOpen(false)} />
      </>
      }
      {tier.footnote && <div className="tier-footnote" data-comment-anchor="f9efc3d47e-div-444-25">{tier.footnote}</div>}
      <div className="cta">
        <a className={'btn ' + (tier.featured ? 'btn--ink' : 'btn--ghost')}
        href={ZZ_SITE_PATHS['signup:' + tier.id] || ZZ_SITE_PATHS.pricing}
        onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('zz-nav', { detail: 'signup:' + tier.id })); }}>
          {tier.cta} <Arrow />
        </a>
      </div>
    </article>);

};

/* ─────────────────────────────────────────────────────────
   THE SPACE - interior photography grid (home & about)
   ───────────────────────────────────────────────────────── */
/* Photos for "the space" grid, made by scripts/seo/import-space-photo.mjs in
   three or four widths. `shape`: hero = the tall tile (two rows on desktop),
   unset = one of the four small squares beside it. (broad / wide add a
   third row of two landscape tiles when there are more than five photos.)
   `focus` is the CSS object-position for when a tile's box is a different
   shape from the photo. The reception arch is deliberately absent: the hero
   at the top of the page already shows it. */
const ZZ_SPACE_TILES = [
  {
    slug: 'sauna-benches', size: 1120, height: 825, shape: 'hero',
    files: ['sauna-benches-480.cbf7fa97.webp', 'sauna-benches-768.3a0d1a6f.webp', 'sauna-benches-1120.b6897b8f.webp'],
    caption: 'the sauna', focus: 'center 55%',
    alt: 'inside the nordic sauna: pale timber benches with warm light glowing beneath them',
  },
  {
    slug: 'entrance-good-things', size: 1120,
    files: ['entrance-good-things-480.f8908ee3.webp', 'entrance-good-things-768.fa74a5aa.webp', 'entrance-good-things-1120.628cd9f0.webp'],
    caption: 'the entrance',
    alt: 'the entrance corridor with an arched door, a wooden counter and the words good things happen here on the wall',
  },
  {
    slug: 'red-light-room', size: 1120,
    files: ['red-light-room-480.fb0f5ff4.webp', 'red-light-room-768.cfbbabcc.webp', 'red-light-room-1120.7e97c829.webp'],
    caption: 'red light room',
    alt: 'the red light room: a futon under a red light panel with a glowing wave-shaped ceiling',
  },
  {
    slug: 'compression-room', size: 1120,
    files: ['compression-room-480.96019f85.webp', 'compression-room-768.688eff29.webp', 'compression-room-1120.eaeac364.webp'],
    caption: 'compression room',
    alt: 'the compression room: normatec boots laid out on a futon beneath a glowing wave-shaped ceiling',
  },
  {
    slug: 'plunge-room', size: 1440,
    files: ['plunge-room-480.83a4f2e7.webp', 'plunge-room-768.60386b6e.webp', 'plunge-room-1120.5a8a9fb8.webp', 'plunge-room-1440.3b4fd42a.webp'],
    caption: 'the plunge room', focus: 'center 45%',
    alt: 'the cold plunge room lit deep blue: two white plunge tubs side by side under the word brrrrr on the wall',
  },
];

export const SSpace = () =>
<section className="space-section">
    <div className="shell">
      <header className="section-head">
        <div data-comment-anchor="70b9677fec-div-462-9">
          <span className="eyebrow eyebrow--accent">the space</span>
          <h2>designed <em>for longevity.</em></h2>
        </div>
        <p className="blurb" data-comment-anchor="7939580574-p-466-9">
          recovery, reimagined through intentional design -
          calm interiors, private spaces, and room to breathe.
        </p>
      </header>
      <div className="space-grid">
        {ZZ_SPACE_TILES.map((t) =>
      <div key={t.slug} className={'space-tile' + (t.shape ? ' space-tile--' + t.shape : '')}>
            <img
          src={`/redesign/assets/imagery/${t.files[1]}`}
          srcSet={t.files.map((f) => `/redesign/assets/imagery/${f} ${f.match(/-(\d+)\./)[1]}w`).join(', ')}
          sizes={t.shape === 'hero' ?
          // the tall tile is 600x648 but its photo is landscape, so the image
          // is scaled to the box height and is ~880px wide on desktop
          '(max-width: 760px) calc(100vw - 40px), (max-width: 1100px) calc(100vw - 64px), 880px' :
          t.shape ?
          '(max-width: 760px) calc(100vw - 40px), (max-width: 1100px) calc(100vw - 64px), 608px' :
          '(max-width: 760px) calc(50vw - 23px), (max-width: 1100px) calc(50vw - 36px), 300px'}
          width={t.size} height={t.height || t.size}
          alt={t.alt}
          style={t.focus ? { objectPosition: t.focus } : undefined}
          loading="lazy" decoding="async" />
            <div className="caption">{t.caption}</div>
          </div>
      )}
      </div>
    </div>
  </section>;


/* ─────────────────────────────────────────────────────────
   EVENTS - private room booking for a group or occasion.
   Otherwise-orphaned pages (birthday, bachelorette, corporate,
   run/marathon/hyrox/crossfit recovery, etc.) get a real
   internal link from the homepage here.
   ───────────────────────────────────────────────────────── */
export const SEvents = ({ onNav }) =>
<section className="events-section">
    <div className="shell">
      <header className="section-head">
        <div>
          <span className="eyebrow eyebrow--accent">private room booking</span>
          <h2>book the room <em>for your event.</em></h2>
        </div>
        <p className="blurb">
          hire the whole space for a birthday, a bachelorette, a team session or a corporate
          off-site - up to 10 people, private to your group, from R600.
        </p>
      </header>
      <div className="events-grid">
        {ZZ_EVENT_LINKS.map((ev) =>
      <a key={ev.id}
      className="event-chip"
      href={ZZ_SITE_PATHS[ev.id]}
      onClick={(e) => { e.preventDefault(); onNav(ev.id); }}>
            {ev.label} <Arrow size={11} />
          </a>
      )}
      </div>
    </div>
  </section>;


/* ─────────────────────────────────────────────────────────
   LOCATIONS - single century city venue. Other openings go
   through the franchise programme.
   ───────────────────────────────────────────────────────── */
export const SLocations = () =>
<section className="locations">
    <div className="shell">
      <header className="section-head">
        <div>
          <span className="eyebrow eyebrow--accent">century city · cape town</span>
          <h2>where <em>to find us.</em></h2>
        </div>
        <p className="blurb">


      </p>
      </header>
      <div className="single-location">
        <div className="img" style={{ backgroundPosition: 'center 58%' }}>
          <div className="badge-corner"><span className="dot"></span>{ZZ_OPEN_LABEL}</div>
        </div>
        <div className="body" data-comment-anchor="86beee9841-div-474-9">
          <span className="city">cape town · 7441</span>
          <h3>century city</h3>
          <p className="addr">5 edison way<br />century city, cape town — inside purefit gym</p>
          <div className="hours-mini">
            <div><span className="d">mon - fri</span><span className="h">06:00 - 20:00</span></div>
            <div><span className="d">sat</span><span className="h">08:30 - 17:00</span></div>
            <div><span className="d">sun</span><span className="h">08:30 - 17:00</span></div>
          </div>
          <div className="foot" data-comment-anchor="6a91116963-div-527-11">
            <a className="btn btn--primary" href={ZZ_SITE_PATHS['signup:monthly']} onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('zz-nav', { detail: 'signup:monthly' })); }}>become a member <Arrow /></a>
            <a className="btn btn--ghost" href={ZZ_SITE_PATHS.app} onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('zz-nav', { detail: 'app' })); }}>open app <Arrow /></a>
          </div>
        </div>
      </div>
    </div>
  </section>;


/* ─────────────────────────────────────────────────────────
   FOOTER
   ───────────────────────────────────────────────────────── */
export const SFooter = ({ onNav, onApp }) => {
  const app = useAppDownload();
  return (
<footer className="foot" data-comment-anchor="b265a0a7fe-footer-498-1">
    <WaveBreak fill="var(--zuzu-big-stone)" edge="footer" />
    <div className="wrap">
      <div className="lead">
        <h2>scan in. <em>leave restored.</em></h2>
        <div className="form-wrap">
          <div className="app-cta" style={{ display: 'flex' }} data-comment-anchor="447df8d66d-input-547-13">
            <a className="btn btn--primary" href={app.href}
               onClick={app.external ? undefined : (e) => { e.preventDefault(); onApp(); }}>download the app <Arrow /></a>
          </div>
          <span className="note">book, scan in, and track your recovery - all from your phone.</span>
        </div>
      </div>
      <div className="cols">
        <div className="col brand">
          <div className="word"><ZuzuStack size={64} /></div>
          <p>a self-service recovery club. cold plunge, sauna, compression, red light, and an inbody read. one address, five rooms, no protocols.</p>
        </div>
        <div className="col">
          <h3>visit</h3>
          <ul data-comment-anchor="b2a1d2cc08-ul-560-11">
            <li><a href={ZZ_SITE_PATHS.contact} onClick={(e) => { e.preventDefault(); onNav('contact'); }}>century city</a></li>
            <li><a href={ZZ_SITE_PATHS.contact} onClick={(e) => { e.preventDefault(); onNav('contact'); }}>opening hours</a></li>
            <li><a href={ZZ_SITE_PATHS.legal} onClick={(e) => { e.preventDefault(); onNav('legal'); }}>house rules</a></li>
            <li><a href={ZZ_SITE_PATHS.modalities} onClick={(e) => { e.preventDefault(); onNav('modalities'); }}>faqs</a></li>
            <li><a href={ZZ_SITE_PATHS.book} onClick={(e) => { e.preventDefault(); onNav('book'); }}>book a slot</a></li>
            <li><a href={ZZ_SITE_PATHS['signup:monthly']} onClick={(e) => { e.preventDefault(); onNav('signup:monthly'); }}>become a member</a></li>
          </ul>
        </div>
        <div className="col">
          <h3>rooms</h3>
          <ul>
            {ZZ_MODALITIES.map((m) =>
          <li key={m.id}><a href={ZZ_SITE_PATHS['modality:' + m.id] || ZZ_SITE_PATHS.modalities} onClick={(e) => { e.preventDefault(); onNav('modality:' + m.id); }}>{m.name}</a></li>
          )}
          </ul>
        </div>
        <div className="col">
          <h3>membership</h3>
          <ul>
            <li><a href={ZZ_SITE_PATHS['signup:drop']} onClick={(e) => { e.preventDefault(); onNav('signup:drop'); }}>drop-in</a></li>
            <li><a href={ZZ_SITE_PATHS['signup:monthly']} onClick={(e) => { e.preventDefault(); onNav('signup:monthly'); }}>monthly member</a></li>
            <li><a href={ZZ_SITE_PATHS['signup:private']} onClick={(e) => { e.preventDefault(); onNav('signup:private'); }}>room booking</a></li>
            <li><a href={ZZ_SITE_PATHS.pricing} onClick={(e) => { e.preventDefault(); onNav('pricing'); }}>gift card</a></li>
          </ul>
        </div>
        <div className="col">
          <h3>events &amp; groups</h3>
          <ul>
            {ZZ_EVENT_LINKS.slice(0, 6).map((ev) =>
          <li key={ev.id}><a href={ZZ_SITE_PATHS[ev.id]} onClick={(e) => { e.preventDefault(); onNav(ev.id); }}>{ev.label}</a></li>
          )}
          </ul>
        </div>
        <div className="col">
          <h3>company</h3>
          <ul>
            <li><a href={ZZ_SITE_PATHS.about} onClick={(e) => { e.preventDefault(); onNav('about'); }}>about</a></li>
            <li><a href={ZZ_SITE_PATHS.app} onClick={(e) => { e.preventDefault(); onApp(); }}>booking app</a></li>
            <li><a href={ZZ_SITE_PATHS.contact} onClick={(e) => { e.preventDefault(); onNav('contact'); }}>press</a></li>
            <li><a href={ZZ_SITE_PATHS.contact} onClick={(e) => { e.preventDefault(); onNav('contact'); }}>contact</a></li>
          </ul>
        </div>
      </div>
      <div className="legal">
        <div>© zuzu recovery club · 2026 · made in cape town</div>
        <div>
          <a href={ZZ_SITE_PATHS.legal} onClick={(e) => { e.preventDefault(); onNav('legal'); }}>terms</a>
          <a href={ZZ_SITE_PATHS.legal} onClick={(e) => { e.preventDefault(); onNav('legal'); }}>privacy</a>
          <a href={ZZ_SITE_PATHS.legal} onClick={(e) => { e.preventDefault(); onNav('legal'); }}>cookies</a>
          <a href={ZZ_SITE_PATHS.contact} onClick={(e) => { e.preventDefault(); onNav('contact'); }}>accessibility</a>
        </div>
      </div>
    </div>
  </footer>);
};



/* ═════════════════════════════════════════════════════════
   PAGE: HOME
   ═════════════════════════════════════════════════════════ */
export const PageHome = ({ heroLayout, photoVariant, onNav }) =>
<>
    <SHero layout={heroLayout} />
    <WaveBreak fill="var(--surface-canvas)" />
    <SModalities photoVariant={photoVariant} onNav={onNav} />
    <WaveBreak fill="var(--surface-warm)" />
    <SFlow />
    <WaveBreak fill="var(--mood-soft)" />
    <SLive />
    <WaveBreak fill="var(--surface-canvas)" />
    <SManifesto />
    <WaveBreak fill="var(--surface-warm)" flip />
    <STiers />
    {/* The tier prices above all hinge on peak vs off-peak, so the schedule
        sits with them here as well as on /pricing. Compact form (no heading
        block) — the homepage doesn't need the full explainer. */}
    <section className="home-peak">
      <div className="shell">
        <SPeakHours heading={false} />
      </div>
    </section>
    <WaveBreak fill="var(--surface-warm)" />
    <SEvents onNav={onNav} />
    <WaveBreak fill="var(--surface-warm)" />
    <SSpace />
    <WaveBreak fill="var(--mood-soft)" />
    <SLocations />
  </>;


/* ═════════════════════════════════════════════════════════
   ROUTER WRAPPER
   ═════════════════════════════════════════════════════════ */
export const SitePage = ({ page, heroLayout, photoVariant, onNav, onApp, onEnterApp, view, onView, onScreen, loggedIn, onAuth, goBack, navDir }) => {
  const pageRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // Which group the drawer should land on when it opens — the events tab in the
  // bottom bar opens it at "events & groups"; everything else opens at the top.
  const [menuSection, setMenuSection] = useState(null);
  const openMenu = (s) => { setMenuSection(typeof s === 'string' ? s : null); setMenuOpen(true); };
  const touch = useRef(null);
  // Scroll-to-top on page change is handled once, in ZuzuRedesign.tsx's
  // [view, page, appScreen] effect, which already covers every page change
  // this component sees. A second scrollTo here (plus a scrollTo(0,0) on
  // .site-page, which isn't an independent scroll container - the window
  // is) was pure forced-reflow: two competing scrollTo calls per navigation,
  // one of which raced and cancelled the other every time.

  // Edge swipe-right → back (native iOS gesture). Only triggers from the
  // left ~28px so it never fights the horizontal card carousels.
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = t && t.clientX <= 28 ? { x: t.clientX, y: t.clientY, t: Date.now() } : null;
  };
  const onTouchEnd = (e) => {
    const s = touch.current;touch.current = null;
    if (!s) return;
    const t = e.changedTouches[0];if (!t) return;
    const dx = t.clientX - s.x,dy = Math.abs(t.clientY - s.y);
    if (dx > 64 && dy < 48 && Date.now() - s.t < 600) {
      const target = mBackTarget(page);
      if (target && goBack) goBack(target);
    }
  };

  // Signup is a focused full-page takeover - no marketing nav / footer.
  if (page && typeof page === 'string' && page.startsWith('signup:')) {
    return (
      <div ref={pageRef} className="site-page" key={page} data-navdir={navDir}>
        <Suspense fallback={null}>
          <PageSignup
            plan={page.slice('signup:'.length)}
            onExit={() => onNav('home')}
            onEnterApp={onEnterApp} />
        </Suspense>
      </div>);
  }
  return (
    <div ref={pageRef} className="site-page" key={page}
    onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Mobile-only app-style sticky header (hidden on desktop via CSS).
                                  Sits OUTSIDE the animated body so it doesn't slide with the page. */}
      <SMobileAppHeader page={page} onNav={onNav} onApp={onApp} onAuth={onAuth}
      onBack={(target) => goBack && goBack(target)} loggedIn={loggedIn}
      onOpenMenu={() => openMenu()} />
      <div className="site-page-body" data-navdir={navDir}>
        <SAnnounce />
        <SNav page={page} onNav={onNav} onApp={onApp} onAuth={onAuth} onOpenMenu={() => openMenu()} />
        {page === 'home' && <PageHome heroLayout={heroLayout} photoVariant={photoVariant} onNav={onNav} />}
        {page === 'modalities' &&
        <Suspense fallback={null}><PageModalities photoVariant={photoVariant} onNav={onNav} /></Suspense>
        }
        {page && typeof page === 'string' && page.startsWith('modality:') &&
        <Suspense fallback={null}>
          <PageModalityDetail modalityId={page.slice('modality:'.length)} onNav={onNav} />
        </Suspense>
        }
        {page === 'book' &&
        <Suspense fallback={null}><PageBook onNav={onNav} /></Suspense>
        }
        {page === 'pricing' &&
        <Suspense fallback={null}><PagePricing /></Suspense>
        }
        {page === 'about' &&
        <Suspense fallback={null}><PageAbout /></Suspense>
        }
        {page === 'contact' &&
        <Suspense fallback={null}><PageContact /></Suspense>
        }
        {page === 'legal' &&
        <Suspense fallback={null}><PageLegal /></Suspense>
        }
        <SFooter onNav={onNav} onApp={onApp} />
      </div>

      {/* Mobile-only: drawer + bottom tab bar. Hidden on desktop via CSS. */}
      <SMobileMenu open={menuOpen} onClose={() => setMenuOpen(false)}
      page={page} onNav={onNav} onApp={onApp} section={menuSection} />
      <SMobileTabBar page={page} onNav={onNav} onApp={onApp} onScreen={onScreen}
      view={view} onView={onView} loggedIn={loggedIn} onOpenMenu={openMenu} />
    </div>);

};
